import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getAutomationFlows, getSurveys, deleteAutomationFlow, deleteSurvey, updateAutomationFlow } from '@/lib/db'
import type { AutomationFlow, Survey } from '@/types'
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Zap,
  ToggleLeft,
  ToggleRight,
  Activity,
  FileBarChart,
  ClipboardList,
  Play
} from 'lucide-react'
import { TRIGGER_META } from './flowConstants'

export default function FlowsList() {
  const [flows, setFlows] = useState<AutomationFlow[]>([])
  const [surveys, setSurveys] = useState<Survey[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'flows' | 'surveys'>('flows')
  const [deleting, setDeleting] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      setLoading(true)
      const [flowsRes, surveysRes] = await Promise.all([getAutomationFlows(), getSurveys()])
      if (flowsRes.data) setFlows(flowsRes.data)
      if (surveysRes.data) setSurveys(surveysRes.data)
      setLoading(false)
    }
    load()
  }, [])

  async function toggleFlow(flow: AutomationFlow) {
    const { data } = await updateAutomationFlow(flow.id, { is_active: !flow.is_active })
    if (data) setFlows(prev => prev.map(f => f.id === flow.id ? { ...f, is_active: !f.is_active } : f))
  }

  async function handleDeleteFlow(id: string) {
    if (!confirm('Eliminar este flujo y todo su historial?')) return
    setDeleting(id)
    await deleteAutomationFlow(id)
    setFlows(prev => prev.filter(f => f.id !== id))
    setDeleting(null)
  }

  async function handleDeleteSurvey(id: string) {
    if (!confirm('Eliminar esta encuesta y todas sus respuestas?')) return
    setDeleting(id)
    await deleteSurvey(id)
    setSurveys(prev => prev.filter(s => s.id !== id))
    setDeleting(null)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="w-7 h-7 text-amber-400" />
            Flujos automaticos
          </h1>
          <p className="text-gray-500 mt-1">
            Automatiza acciones cuando ocurren eventos en la plataforma
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/flows/logs"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 text-sm font-medium"
          >
            <Activity className="w-4 h-4" />
            Historial
          </Link>
          {tab === 'flows' ? (
            <Link
              to="/admin/flows/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Crear flujo
            </Link>
          ) : (
            <Link
              to="/admin/surveys/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Crear encuesta
            </Link>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('flows')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            tab === 'flows' ? 'bg-[#0055A4] text-white' : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          <Zap className="w-4 h-4" /> Flujos ({flows.length})
        </button>
        <button
          onClick={() => setTab('surveys')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            tab === 'surveys' ? 'bg-[#0055A4] text-white' : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          <ClipboardList className="w-4 h-4" /> Encuestas ({surveys.length})
        </button>
      </div>

      {/* Flows tab */}
      {tab === 'flows' && (
        <>
          {flows.length === 0 ? (
            <EmptyState icon={Zap} title="Sin flujos" description="Crea tu primer flujo automático para responder a eventos de la plataforma." />
          ) : (
            <div className="space-y-3">
              {flows.map(flow => {
                const trigger = TRIGGER_META[flow.trigger_type]
                const actionsCount = flow.actions?.length || 0
                return (
                  <div key={flow.id} className={`bg-white rounded-xl border p-4 transition-colors ${flow.is_active ? 'border-gray-200' : 'border-gray-800 opacity-60'}`}>
                    <div className="flex items-center gap-4">
                      {/* Toggle */}
                      <button onClick={() => toggleFlow(flow)} className="flex-shrink-0" title={flow.is_active ? 'Desactivar' : 'Activar'}>
                        {flow.is_active
                          ? <ToggleRight className="w-8 h-8 text-green-700" />
                          : <ToggleLeft className="w-8 h-8 text-gray-600" />
                        }
                      </button>

                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${flow.is_active ? 'bg-[#0055A4]/10' : 'bg-gray-100'}`}>
                        <span className="text-lg">{trigger?.icon || '?'}</span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{flow.name}</h3>
                        <p className="text-sm text-gray-500 truncate">
                          {trigger?.label || flow.trigger_type} &rarr; {actionsCount} acción{actionsCount !== 1 ? 'es' : ''}
                        </p>
                      </div>

                      {/* Stats */}
                      <div className="hidden sm:flex items-center gap-4 text-sm text-gray-500 flex-shrink-0">
                        <span className="flex items-center gap-1" title="Ejecuciones">
                          <Play className="w-3.5 h-3.5" /> {flow.executions_count}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100">
                          {flow.scope_type === 'all' ? 'Todos' : flow.scope_type === 'cohort' ? 'Cohorte' : 'Individual'}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Link to={`/admin/flows/${flow.id}/edit`} className="p-2 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-100">
                          <Pencil className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDeleteFlow(flow.id)}
                          disabled={deleting === flow.id}
                          className="p-2 text-gray-500 hover:text-red-600 rounded-lg hover:bg-gray-100"
                        >
                          {deleting === flow.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {flow.description && (
                      <p className="text-xs text-gray-500 mt-2 ml-[4.5rem]">{flow.description}</p>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* Surveys tab */}
      {tab === 'surveys' && (
        <>
          {surveys.length === 0 ? (
            <EmptyState icon={ClipboardList} title="Sin encuestas" description="Crea encuestas para enviar automaticamente a los alumnos via flujos." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {surveys.map(survey => (
                <div key={survey.id} className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <FileBarChart className="w-5 h-5 text-indigo-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{survey.title}</h3>
                        <p className="text-xs text-gray-500">{survey.questions.length} preguntas</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${survey.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {survey.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                  {survey.description && <p className="text-sm text-gray-500 mb-3">{survey.description}</p>}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{survey.responses_count} respuestas</span>
                    <div className="flex items-center gap-1">
                      <Link to={`/admin/surveys/${survey.id}/edit`} className="p-2 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-100">
                        <Pencil className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDeleteSurvey(survey.id)}
                        disabled={deleting === survey.id}
                        className="p-2 text-gray-500 hover:text-red-600 rounded-lg hover:bg-gray-100"
                      >
                        {deleting === survey.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function EmptyState({ icon: Icon, title, description }: { icon: any; title: string; description: string }) {
  return (
    <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
      <Icon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
      <p className="text-lg font-medium text-gray-600">{title}</p>
      <p className="text-gray-500 mt-1">{description}</p>
    </div>
  )
}
